import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';

import { trackConversion, trackMetaEvent, CONVERSIONS } from '../lib/gtag';
import { calcPrivate, calcBusiness } from '../lib/calculator';
/**
 * CartContext — globaler Warenkorb-State für den FuelX-Checkout-Drawer + Warenkorb-Seite.
 * Payload `config`: mode, vehicles, annualKm, employees, cardTier, cardFee, tankCredit.
 * Persistiert via localStorage (v3 — ältere Versionen enthielten Add-Ons und werden ignoriert).
 */

const DEFAULT_CONFIG = {
  mode: 'private',
  vehicles: 1,
  annualKm: 15000,
  employees: 0,
  householdAdults: 1,
  householdVehicles: 1,
  cardTier: 'silver',
  proRatedPrice: 99.90,
};

const STORAGE_KEY = 'fuelx_cart_config_v3';
const HAS_ITEMS_KEY = 'fuelx_cart_has_items_v3';
const TOUCHED_KEY = 'fuelx_cart_touched_v3';

/** Warenkorb-Wert (Jahrespreis) aus der Config — für AddToCart/InitiateCheckout value. */
const cartValue = (c) => {
  try {
    if (c?.mode === 'business') return calcBusiness({ vehicles: c.vehicles, annual_km: c.annualKm, employees: c.employees }).card_fee;
    return calcPrivate({ vehicles: 1, annual_km: c?.annualKm }).card_fee;
  } catch { return Number(c?.proRatedPrice) || 0; }
};

const loadPersisted = () => {
  if (typeof window === 'undefined') return { config: DEFAULT_CONFIG, hasItems: false, touched: false };
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    const hasItems = window.localStorage.getItem(HAS_ITEMS_KEY) === '1';
    const touched = window.localStorage.getItem(TOUCHED_KEY) === '1';
    if (!raw) return { config: DEFAULT_CONFIG, hasItems, touched };
    return { config: { ...DEFAULT_CONFIG, ...JSON.parse(raw) }, hasItems, touched };
  } catch {
    return { config: DEFAULT_CONFIG, hasItems: false, touched: false };
  }
};

const CartContext = createContext({
  isOpen: false,
  config: DEFAULT_CONFIG,
  hasItems: false,
  touched: false,
  openCart: () => {},
  closeCart: () => {},
  setConfig: () => {},
  clearCart: () => {},
});

export function CartProvider({ children }) {
  const [isOpen, setIsOpen] = useState(false);
  const [config, setConfigState] = useState(() => loadPersisted().config);
  const [hasItems, setHasItems] = useState(() => loadPersisted().hasItems);
  const [touched, setTouched] = useState(() => loadPersisted().touched);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.setItem(TOUCHED_KEY, touched ? '1' : '0');
    } catch (e) { console.warn('[Cart] Persistenz fehlgeschlagen', e); }
  }, [touched]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(config));
    } catch (e) { console.warn('[Cart] Persistenz fehlgeschlagen (quota?)', e); }
  }, [config]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.setItem(HAS_ITEMS_KEY, hasItems ? '1' : '0');
    } catch (e) { console.warn('[Cart] Persistenz fehlgeschlagen', e); }
  }, [hasItems]);

  const setConfig = useCallback((next) => {
    setConfigState((prev) => (typeof next === 'function' ? next(prev) : { ...prev, ...next }));
    setTouched(true);
  }, []);

  const openCart = useCallback((nextConfig) => {
    const merged = nextConfig ? { ...config, ...nextConfig } : config;
    if (nextConfig) { setConfigState((prev) => ({ ...prev, ...nextConfig })); setTouched(true); }
    const wasEmpty = !hasItems;
    setHasItems(true);
    setIsOpen(true);
    // AddToCart NUR beim ersten Befüllen eines Warenkorbs feuern — nicht bei jedem erneuten
    // Öffnen des Drawers/Variantenwechsel. `once`-Guard dedupliziert zusätzlich pro Session.
    if (wasEmpty) {
      const value = cartValue(merged);
      const cardName = merged?.mode === 'business' ? 'Gold Card' : 'Silver Card';
      trackConversion(CONVERSIONS.ADD_TO_CART, { value, currency: 'EUR', once: 'add_to_cart' });
      trackMetaEvent('AddToCart', {
        once: 'add_to_cart',
        params: { value, currency: 'EUR', content_type: 'product', content_name: cardName },
      });
    }
  }, [config, hasItems]);

  const closeCart = useCallback(() => setIsOpen(false), []);

  // Deep-Link: /?cart=1 (oder #warenkorb) öffnet den Checkout-Drawer direkt — z. B. zum Teilen/Testen auf Mobil.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.get('cart') === '1' || window.location.hash === '#warenkorb') {
        setHasItems(true);
        setIsOpen(true);
      }
    } catch (e) { /* noop */ }
  }, []);

  const clearCart = useCallback(() => {
    setConfigState(DEFAULT_CONFIG);
    setHasItems(false);
    setTouched(false);
    setIsOpen(false);
    if (typeof window !== 'undefined') {
      try {
        window.localStorage.removeItem(STORAGE_KEY);
        window.localStorage.removeItem(HAS_ITEMS_KEY);
        window.localStorage.removeItem(TOUCHED_KEY);
        // Funnel-Event-Guards zurücksetzen → ein NEUER Warenkorb darf AddToCart/InitiateCheckout
        // wieder genau einmal messen (kein Verlust echter neuer Funnel-Instanzen).
        ['fbconv_add_to_cart', 'gconv_add_to_cart', 'fbconv_initiate_checkout', 'gconv_initiate_checkout', 'fbconv_add_payment_info']
          .forEach((k) => { try { window.sessionStorage.removeItem(k); } catch { /* noop */ } });
      } catch (e) { console.warn('[Cart] Cleanup fehlgeschlagen', e); }
    }
  }, []);

  return (
    <CartContext.Provider value={{ isOpen, config, hasItems, touched, openCart, closeCart, setConfig, clearCart }}>
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  return useContext(CartContext);
}

export { DEFAULT_CONFIG };
